// Main exports for @korvo/core package

// KorvoChat component and types
export { KorvoChat } from './components/shared/KorvoChat'
export type {
  ChatMessage,
  KorvoChatConfig,
  KorvoChatProps,
  ToolCall,
} from './components/shared/KorvoChat/types'

// Supporting components
export { QRCode } from './client/components/shared/QRCode'
export { ErrorBoundary } from './components/shared/ErrorBoundary'
export { LoadingSpinner } from './components/shared/LoadingSpinner'
export { NotificationCenter } from './components/shared/NotificationCenter'
export { SessionTimeout } from './components/shared/SessionTimeout'
export { UserAvatarBubble } from './components/shared/UserAvatarBubble'
// Composable building blocks (for derivative app pages/admin)
export {
  DataTable,
  DataTableBody,
  DataTableHeader,
  DataTableRow,
} from './components/shared/DataTable'
export type { FormFieldProps } from './components/shared/FormField'
export { FormField } from './components/shared/FormField'
export type { PageHeaderProps } from './components/shared/PageHeader'
export { PageHeader } from './components/shared/PageHeader'
export { Badge as StatusBadge } from './components/ui/badge'
export { Card as AdminCard, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card'

// Providers
export { SessionProvider } from './components/providers/SessionProvider'
export { ToastProvider, useToastContext } from './components/shared/Toast/ToastProvider'

// Help page (with slots for tier-2 customization)
export type { HelpPageProps,HelpPageSlots } from './components/help/HelpPage'
export { HelpPage } from './components/help/HelpPage'

// Profile components
export {
  ActivityHistory,
  AvatarUpload,
  EmailChangeForm,
  MobileProfileHeader,
  ModuleAnalytics,
  PasswordChangeForm,
  PrivacyControls,
  ProfileCompletion,
  ProfileEditForm,
  UserProfile,
} from './components/profile'
export type { UserProfileTabId } from './components/profile/UserProfile'

// Chat architecture components (if needed)
export { ChatView } from './components/shared/Chat'
export type { ChatInstanceConfig, ChatInstanceState } from './components/shared/Chat/architecture/types'

// Hooks
export { useAuth } from './client/hooks/useAuth'
export { useCurrentUserProfile } from './client/hooks/useCurrentUserProfile'
export { useFocusTrap } from './client/hooks/useFocusTrap'
export { useIsAdminForMessages } from './client/hooks/useIsAdminForMessages'
export { useKeyboard } from './client/hooks/useKeyboard'
export { useProfileUpdates } from './client/hooks/useProfileUpdates'
export { useRequireAuth,useSessionValidation } from './client/hooks/useSessionValidation'
export { useToast } from './client/hooks/useToast'
// useTopBarKeyboardNavigation removed - depends on topbar-service which is not migrated

// Utils
export { getBaseClientTools } from './utils/base-client-tools'
// registerKorvoBaseTools is server-only - import from '@korvo/core/tools/base-tools' in server-side code only
export {
  generateQRCodeDataURL,
  generateQRCodeSVG,
  generateRegistrationCodeQRCode,
} from './utils/qr-code'

// Types
export { mapPortalThreadListRowToChatSession } from './components/shared/Chat/lib/map-portal-thread-list-row'
export type { ChatSession, ChatSessionManager } from './components/shared/Chat/types/session-manager'
export type { ModuleSettingsConfig, PortalConfig, SystemSettings } from './shared/types/types/settings'
export type { Workspace } from './shared/types/types/workspace'

// Portal client (KorvoThreadsClient is module-aware; for plain SDK client use @korvo/portal-sdk/threads-client)
export { getPortalApiBase, KorvoThreadsClient } from './ai/portal/threads-client'
export { useThread } from './ai/portal/threads-client'

// Lib utilities
export type { ErrorContext } from './lib/error-tracking-client'
export { errorTracker, setupGlobalErrorHandling, withErrorTracking } from './lib/error-tracking-client'
export { sanitizeHtml } from './lib/html-sanitizer'
// loadRagModule is server-only; import from '@korvo/core/lib/rag-loader' to avoid pulling optional RAG into client bundles
// Unauthenticated landing component for unauthenticated homepage experience
export { UnauthenticatedLanding } from './components/homepage/UnauthenticatedLanding'
export {
  hasRole,
  isAdmin,
  isAdminUser,
  type MaybeUserWithRole,
  normalizeRole,
  ROLES,
} from './lib/rbac'
export { applyTheme, defaultTheme,toggleDarkMode } from './lib/theme'
export { cn, formatRelativeTime } from './lib/utils'
// Database functions are server-only - import from '@korvo/core/lib/global-database-manager' directly in server components/API routes

// Client-safe session validation functions
export { clearInvalidatedUsers, getInvalidatedUsers, invalidateMultipleUserSessions, invalidateUserSessions, isSessionValid, isUserSessionInvalidated, securityInvalidateUser } from './lib/session-validation'

// Notification types (client-safe)
export type { Notification, NotificationCreate } from './lib/notifications-types'

// Server-side utilities (server-only - import directly from specific paths to prevent client bundling)
// These are NOT exported from main index because they have Prisma dependencies
// Import from '@korvo/core/lib/notifications' for notification service
// Import from '@korvo/core/lib/audit' for audit logging  
// Import from '@korvo/core/lib/session-security' for validateUserExists
// Import from '@korvo/core/lib/rate-limit' for rate limiting
// Import from '@korvo/core/lib/db-optimization' for database optimization

// Better Auth helpers
export type { Session as BetterAuthSession, User as BetterAuthUser } from './lib/better-auth/client'
export { authClient, signIn, signOut, signUp, useSession, useSignIn, useSignOut,useSignUp } from './lib/better-auth/client'
// Server-side auth helpers - import from '@korvo/core/lib/better-auth/server' directly
// Or use '@korvo/core/derivative-app/auth-helpers' which provides wrappers
// Password utilities are server-only - import from '@korvo/core/lib/better-auth-password' directly in API routes

// Types
export type { Session, User } from './types/session'

// Constants
export { API_TIMEOUTS, CONTENT_LIMITS,RATE_LIMIT_WINDOWS, UI_TIMEOUTS } from './shared/constants/timeouts'

// Test utilities — exported via @korvo/core/test-utils, not the main barrel,
// to avoid pulling jest types into production builds.

// Utilities tools - NOT exported from main index to prevent Playwright from being bundled in client
// Import directly from '@korvo/core/tools/utilities' in server-side code only
// export {
//   registerUtilityTools,
//   registerUtilityToolsDefault,
//   registerUtilityToolsWithSettings,
//   unregisterUtilityTools,
// } from './tools/utilities'